// The executor's typed failure. Consumers match on `kind` + `detail`; `message` is the legacy string shown to a
// model or persisted beside its `actionErrorRecord`.
import { absentTargetError, ambiguousTargetError, notEditableTargetError } from '../target-errors.ts'
import { recordedTargetAbsentMessage, recordedTargetDisabledMessage } from '../recorded-target-wait.ts'
import type { ElementType } from './action.ts'
import type { Choice, ChoiceFault, Decided, NodeRef, Obstruction, OccluderFacts, Occlusion, OcclusionDecision, OcclusionEnd, OffSwitch, Phase } from './dispatch-facts.ts'

/** A page element as evidence in an error (the focused element). */
export interface ElementDesc { tag: string; text?: string }

const INPUT_VERB_LIST = [
  'keyboard.press', 'keyboard.down', 'keyboard.up', 'keyboard.type', 'mouse.move', 'mouse.down', 'mouse.up', 'mouse.click',
  'click', 'hover', 'fill', 'press', 'selectText', 'selectOption', 'setInputFiles', 'dispatchEvent', 'focus',
] as const
export type InputVerb = (typeof INPUT_VERB_LIST)[number]
/** Every op verb: the input verbs, then the probe verbs. */
export const OP_VERBS = [
  ...INPUT_VERB_LIST,
  'count', 'isDisabled', 'evaluate', 'evaluateAll', 'evaluateHandle', 'boundingBox', 'elementHandle',
  'readClipboard', 'readOpfs', 'readValue', 'getAttribute', 'inputValue', 'textContent', 'scrollIntoViewIfNeeded', 'waitForLoadState', 'waitForTimeout',
  'goto', 'goBack', 'scroll', 'revealTogether', 'dispose',
] as const
export type OpVerb = (typeof OP_VERBS)[number]

const INPUT_VERBS: ReadonlySet<OpVerb> = new Set<InputVerb>(INPUT_VERB_LIST)
export const isInputVerb = (verb: OpVerb): boolean => INPUT_VERBS.has(verb)

/** One browser operation: what a driver failure is about, and what the run records (./record.ts). `value`: the
 * entered text, key, option, file names, URL or scroll; redacted when recorded. */
export interface SemanticOp { verb: OpVerb; selector?: string; value?: string }

/** A throw from outside the taxonomy (Playwright, the page, a helper), as data. The thrown value is the ActionError's `cause`. */
export interface ForeignError { name: string; message: string }
export const foreignError = (e: unknown): ForeignError => e instanceof Error ? { name: e.name, message: e.message } : { name: typeof e, message: String(e) }

/** Why a target takes no typed text: it is no text field, or a read-only one (Playwright's `editable` state). */
export type NotEditableWhy = 'notTextField' | 'readonly'

/** The field a NotEditable names: its selector, or, for a type/input without one, the typed value. */
export type EntryField = { selector: string } | { selector: null; typed: string }

/** What a selector failed to name: the action's target, a select's option (`option`: the step's value), a drag's drop
 * target, a control chosen to remove a layer covering the target (./choice.ts). */
export type TargetOf = { of: 'target' | 'dropTarget' | 'remedyControl' } | { of: 'option'; option: string }

export interface Detail {
  Invalid: { field: 'readFrom' | 'value' | 'keys' | 'selector' | 'destination' | 'file' | 'variable'; reason: string }
  CredentialUnavailable: { tokens: string[] }
  // `phase`: the dispatch stage that measured the fact.
  TargetAbsent: { selector: string; phase: Phase } & TargetOf
  TargetAmbiguous: { selector: string; count: number; phase: Phase } & TargetOf
  TargetDisabled: { selector: string }
  // `scrollIntoView`: the scrollIntoView action's selector; its message names it.
  TargetUnreachable: { why: 'invisible' | 'offViewport' | 'clipped'; scrollIntoView?: string; phase: Phase }
  // `action`: the covered-control blocker form is the click's (design §3).
  TargetObstructed: { occluder: Obstruction; action: ElementType; phase: Phase }
  // The hit at the target's point is its own ancestor and nothing beside it takes the pointer (an OccluderFacts
  // `self`): its off switch, else `ancestorPaint` (no switch measured: the ancestor paints over it).
  TargetNotHittable: { cause: OffSwitch | 'ancestorPaint'; phase: Phase }
  // The gesture's actionability wait ended on a target whose box kept moving (a self-moving hover target).
  TargetUnstable: { selector: string; phase: Phase }
  // `selector: null`: a type/input without a selector met a focused element that takes no text (#5155).
  NotEditable: { why: NotEditableWhy } & EntryField
  FocusNotAcquired: { selector: string; active: ElementDesc }
  // `fillable`: the field takes a value by fill (recovery/value-not-landed.heuristics.ts).
  EffectAbsent: { effect: 'value'; fillable: boolean } | { effect: 'fileDialog'; selector: string } | { effect: 'opfsEntry'; path: string; stored: string[] } | { effect: 'fragment' | 'history' | 'menuDismissal' }
  NavigationRefused: { target: string }
  NavigationFailed: { url: string; cause: ForeignError }
  NotExecutable: { type: string }
  DriverTimeout: { op: SemanticOp; cause: ForeignError }
  DriverStall: { op: SemanticOp; ms: number }
  DispatchUnacknowledged: { op: SemanticOp; ms: number }
  ActionDeadlineExceeded: { op: SemanticOp; budgetMs: number; elapsedMs: number; issued: boolean }
  DriverError: { op?: SemanticOp; cause: ForeignError }
}
export type Kind = keyof Detail

const describe = ({ tag, text }: ElementDesc): string => `<${tag}>${text ?? ''}</${tag}>`
// A NodeRef's opening tag: its role and accessible naming as attributes (`labelledby`: its aria-labelledby text).
function openingTag({ tag, role, ariaLabel, name }: NodeRef): string {
  const attrs = ([['role', role], ['aria-label', ariaLabel], ['labelledby', name]] as const).map(([k, v]) => v === null ? '' : ` ${k}=${JSON.stringify(v)}`)
  return `<${tag}${attrs.join('')}>`
}
const describeNode = (n: NodeRef): string => `${openingTag(n)}${n.text}</${n.tag}>`

/** An obstruction's occluder as markup: its hit, and its layer when it has one. */
export function describeOccluder(o: Obstruction): string {
  if (o.kind === 'escape') return `${describeNode(o.hit)} (the target takes no pointer events; ${openingTag(o.layer)} takes them)`
  return o.layer ? `${describeNode(o.hit)} from ${openingTag(o.layer)} subtree` : describeNode(o.hit)
}

// Why a chosen control is no remedy, per fault.
const CHOICE_FAULT: Record<ChoiceFault, string> = {
  absent: 'it is not on the page', ambiguous: 'it names several elements', unrendered: 'it is not visible',
  notHittable: 'another element covers it', containsTarget: 'it contains the control itself', outsideLayer: 'it is not part of the covering UI',
}

/** How a rendered remedy names its clicked control: as an observed control, or generically. */
export type ControlNaming = 'generic' | { label: string; description: string }

function remedyBy(choice: Choice, naming: ControlNaming): string {
  if ('key' in choice) return 'pressing Escape'
  if ('none' in choice) return 'no remedy'
  return naming === 'generic' ? 'clicking a control of it' : `clicking ${naming.label} (${naming.description})`
}

/** What a decided remedy did about its occluder. */
export function remedyOutcome(d: Decided, naming: ControlNaming): string {
  const by = remedyBy(d.choice, naming)
  switch (d.outcome) {
    case 'cleared': return d.input ? `removed by ${by}` : `gone by itself before ${by}`
    case 'persisted': return d.input ? `still covering after ${by}` : `gone by itself before ${by}, then covering again`
    case 'declined': return 'left in place: no remedy chosen'
    case 'invalidChoice': return `${by} refused as its remedy: ${CHOICE_FAULT[d.fault]}`
    case 'failed': return d.failure ? `${by} failed: ${d.failure.kind}` : `${by} did not complete`
  }
}

function decisionText(d: OcclusionDecision, naming: ControlNaming): string {
  if ('decided' in d) return remedyOutcome(d.decided, naming)
  if (d.undecided === 'chooserError') return `left in place: the remedy decision failed (${d.error.message})`
  return d.undecided === 'noChooser' ? 'left in place: no remedy was asked for' : 'left in place: no attempt was left for a remedy'
}

const ENDED: Record<OcclusionEnd, string> = { repositioned: 'scrolled clear of it', blocked: 'blocked an attempt' }

const SEEN_AT: Record<Phase, string> = {
  settle: 'while the target settled', reach: 'as the pointer reached the target', preGesture: 'just before the click',
  preEffect: 'just before the input', gesture: 'during the pointer gesture', effect: 'after the input was sent', remedy: 'during a remedy',
}

// A layer beside the target, or the target taking no pointer events where the pointer lands on its container.
function occluding(o: OccluderFacts): string {
  if (o.kind === 'self') return `the target takes no pointer events (${NOT_HITTABLE[o.offSwitch?.by ?? 'ancestorPaint']}); the pointer lands on its container ${describeNode(o.hit)}`
  const traits = [o.modal && 'modal', o.coversViewport && 'covers the viewport', o.chrome && 'pinned page chrome'].filter((t) => t !== false)
  return `${describeOccluder(o)}${traits.length ? ` (${traits.join(', ')})` : ''}${o.kind === 'layer' ? ' covered the target' : ''}`
}

/** An occlusion as one line: what occluded the target, when it was first seen, and what the dispatch did about it. */
export function occlusionText({ occluder, phase, ended, decision }: Occlusion, naming: ControlNaming): string {
  const outcome = decision ? decisionText(decision, naming) : ended ? ENDED[ended] : 'not in the way'
  return `${occluding(occluder)} ${SEEN_AT[phase]}: ${outcome}`
}

/** Why a TargetNotHittable target takes no pointer events, per cause. */
export const NOT_HITTABLE: Record<Detail['TargetNotHittable']['cause'], string> = {
  pointerEventsNone: 'pointer-events:none on it or its ancestor',
  inert: 'it or its ancestor is inert',
  ancestorPaint: 'its ancestor paints over it',
}
const INPUT_MAY_LAND = 'input may have been sent and may still take effect'
const notSettled = (op: SemanticOp, ms: number): string =>
  `${op.verb} did not settle within ${ms}ms: ${isInputVerb(op.verb) ? INPUT_MAY_LAND : "the page's renderer is stalled or the target left the page"}`
const UNREACHABLE: Record<Detail['TargetUnreachable']['why'], string> = {
  invisible: 'element is not visible',
  offViewport: 'element is outside of the viewport',
  clipped: 'element is clipped out of view by a scrolling ancestor',
}
// The stored paths a missed OPFS read names, so a recovery can re-read one the app actually wrote.
const OPFS_LISTED = 20
function opfsHoldings(stored: string[]): string {
  if (!stored.length) return 'it holds no files'
  const more = stored.length > OPFS_LISTED ? `, and ${stored.length - OPFS_LISTED} more` : ''
  return `the files it holds are ${stored.slice(0, OPFS_LISTED).map(p => JSON.stringify(p)).join(', ')}${more}`
}
const EFFECT_ABSENT: Record<Exclude<Detail['EffectAbsent']['effect'], 'fileDialog' | 'opfsEntry'>, string> = {
  value: 'the typed value did not land in the field',
  fragment: 'Enter on the visually-hidden fragment anchor moved neither the URL fragment nor focus to its target',
  history: 'going back changed no history entry',
  menuDismissal: 'clicking the expanded menu trigger did not collapse it',
}
/** The covered-control blocker message: a click a user could not make on a control behind a blocking overlay. */
function coveredControlBlocker(overlayKind: string, driverError: string): string {
  return `Target control is currently covered by ${overlayKind}. Original driver error: ${driverError}`
}

const dropTargetUnresolved = (selector: string): string => `drag: drop target could not be resolved (${selector})`
const remedyControlUnresolved = (selector: string): string => `the control chosen to remove the covering layer does not name one element (${selector})`

const MESSAGE: { [K in Kind]: (detail: Detail[K]) => string } = {
  Invalid: ({ reason }) => reason,
  CredentialUnavailable: ({ tokens }) =>
    `no credential available for ${tokens.join(', ')} — this project supplied no login credentials, so this login/auth field cannot be filled; treat the login wall as the obstacle rather than typing an invented value`,
  // Settled: one message both legacy absent predicates recognize (design §3). Only the action's own target
  // takes the target forms, which the consumers re-resolve.
  TargetAbsent: (d) => {
    if (d.of === 'dropTarget') return dropTargetUnresolved(d.selector)
    if (d.of === 'remedyControl') return remedyControlUnresolved(d.selector)
    if (d.of === 'option') return `select: ${d.selector} has no option whose label or value is ${JSON.stringify(d.option)}`
    return d.phase === 'settle' ? `${absentTargetError(d.selector)} — ${recordedTargetAbsentMessage(d.selector)}` : absentTargetError(d.selector)
  },
  TargetAmbiguous: (d) => {
    if (d.of === 'dropTarget') return dropTargetUnresolved(d.selector)
    if (d.of === 'remedyControl') return remedyControlUnresolved(d.selector)
    if (d.of === 'option') return `select: ${d.count} options of ${d.selector} have the label or value ${JSON.stringify(d.option)}`
    return ambiguousTargetError(d.count, d.selector)
  },
  TargetDisabled: ({ selector }) => recordedTargetDisabledMessage(selector),
  TargetUnreachable: ({ why, scrollIntoView }) => {
    if (scrollIntoView === undefined) return UNREACHABLE[why]
    return why === 'invisible'
      ? `scroll target ${scrollIntoView} has no visible box (display:none, visibility:hidden or zero size)`
      : `scroll target ${scrollIntoView} is not in view after scrolling to it`
  },
  TargetObstructed: ({ occluder, action }) => {
    const intercepts = `${describeOccluder(occluder)} intercepts pointer events`
    if (action !== 'click' || !(occluder.modal || occluder.coversViewport)) return intercepts
    return coveredControlBlocker(occluder.modal ? 'an open modal/detail overlay' : 'a full-viewport blocking overlay', intercepts)
  },
  TargetNotHittable: ({ cause }) => `element does not receive pointer events: ${NOT_HITTABLE[cause]}`,
  TargetUnstable: ({ selector }) => `element is not stable: ${selector} kept moving through the gesture's actionability wait`,
  NotEditable: (d) => {
    if (d.why === 'readonly') return `type: ${d.selector === null ? 'the focused field' : d.selector} is read-only (readonly / aria-readonly), so it is not editable — choose an editable field`
    return d.selector === null
      ? `type: no selector was provided and the focused element is not a text field, so "${d.typed}" would be typed into the wrong control — re-resolve the input's selector`
      : notEditableTargetError()
  },
  FocusNotAcquired: ({ selector, active }) =>
    `focusing ${selector} left focus on ${describe(active)}, so the keys would land in the wrong control`,
  EffectAbsent: (d) => {
    if (d.effect === 'fileDialog') return `uploadFile: clicking ${d.selector} opened no file dialog, so no file was attached — the control does not reach a file picker`
    if (d.effect === 'opfsEntry') return `read_variable: the origin-private file system has no file at ${JSON.stringify(d.path)} — ${opfsHoldings(d.stored)}`
    return EFFECT_ABSENT[d.effect]
  },
  NavigationRefused: ({ target }) => `Refusing to navigate to ${target}`,
  NavigationFailed: ({ cause }) => cause.message,
  NotExecutable: ({ type }) => type === 'callTool'
    ? 'callTool must be resolved to a pinned tool revision before execution'
    : `"${type}" is not an executable action type — the runner must resolve or expand it before execution`,
  DriverTimeout: ({ cause }) => cause.message,
  DriverStall: ({ op, ms }) => notSettled(op, ms),
  DispatchUnacknowledged: ({ op, ms }) => notSettled(op, ms),
  ActionDeadlineExceeded: ({ op, budgetMs, elapsedMs, issued }) => issued
    ? `${op.verb} did not settle within the ${budgetMs}ms action deadline${isInputVerb(op.verb) ? `: ${INPUT_MAY_LAND}` : ''}`
    : `${op.verb} not issued: action deadline spent (${elapsedMs}ms of ${budgetMs}ms)`,
  DriverError: ({ cause }) => cause.message,
}

export class ActionError<K extends Kind = Kind> extends Error {
  constructor(readonly kind: K, readonly detail: Detail[K], options?: ErrorOptions) {
    super(MESSAGE[kind](detail), options)
    this.name = 'ActionError'
  }
}

export const hasKind = <K extends Kind>(e: ActionError, kind: K): e is ActionError<K> => e.kind === kind

/** An ActionError as a record persists it. */
export interface ActionErrorRecord<K extends Kind = Kind> { kind: K; detail: Detail[K] }

// An op's `value` is recorded only redacted, by its operation record (./record.ts).
const opRef = ({ verb, selector }: SemanticOp): SemanticOp => selector === undefined ? { verb } : { verb, selector }
const RECORDED: { [K in Kind]?: (detail: Detail[K]) => Detail[K] } = {
  DriverTimeout: (d) => ({ ...d, op: opRef(d.op) }),
  DriverStall: (d) => ({ ...d, op: opRef(d.op) }),
  DispatchUnacknowledged: (d) => ({ ...d, op: opRef(d.op) }),
  ActionDeadlineExceeded: (d) => ({ ...d, op: opRef(d.op) }),
  DriverError: (d) => d.op ? { ...d, op: opRef(d.op) } : d,
}

export function actionErrorRecord<K extends Kind>({ kind, detail }: ActionError<K>): ActionErrorRecord<K> {
  return { kind, detail: RECORDED[kind]?.(detail) ?? detail }
}

// The phases measured once the effect op was issued.
const EFFECT_ISSUED: ReadonlySet<Phase> = new Set(['gesture', 'effect'])

// Whether the action failed before sending its effect input (the click, the hover's pointer, the keys, the files,
// the selection, the drag, the navigation). Preparatory input (the pointer's approach, a focusing click) may have
// been sent. A kind that can follow the effect input, or leaves it uncertain, counts as dispatched.
const UNDISPATCHED: { [K in Kind]: (detail: Detail[K]) => boolean } = {
  Invalid: () => true,
  CredentialUnavailable: () => true,
  NotExecutable: () => true,
  NavigationRefused: () => true,
  // A target gone once the effect op was issued may be gone by its effect.
  TargetAbsent: (d) => !EFFECT_ISSUED.has(d.phase),
  TargetAmbiguous: (d) => !EFFECT_ISSUED.has(d.phase),
  TargetDisabled: () => true,
  // A gesture's re-measure names why its actionability wait sent no input; a scrollIntoView target not in view
  // after its scroll is that scroll's outcome.
  TargetUnreachable: (d) => d.phase !== 'effect',
  TargetObstructed: (d) => d.phase !== 'effect',
  TargetNotHittable: (d) => d.phase !== 'effect',
  TargetUnstable: (d) => d.phase !== 'effect',
  NotEditable: () => true,
  FocusNotAcquired: () => true,
  EffectAbsent: () => false,
  NavigationFailed: () => false,
  DriverTimeout: () => false,
  DriverStall: () => false,
  DispatchUnacknowledged: () => false,
  ActionDeadlineExceeded: () => false,
  DriverError: () => false,
}

/** The action's effect input never reached the page, so it changed nothing (DiscoveryAction.unreachable). */
export function isUndispatched<K extends Kind>(e: ActionError<K>): boolean {
  return UNDISPATCHED[e.kind](e.detail)
}
